import { useAgreeAiService } from '@/context/services/ai_service_setting/AiServiceSetting.service';
import type { AiService } from '@/context/services/ai_service_setting/type';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  Caption,
  Heading,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import { Divider } from 'antd';
import classnames from 'classnames/bind';
import { FC, useCallback, useState } from 'react';
import { ModelTrainingSetting } from '../ModelTrainingSetting/ModelTrainingSetting';
import { AiAgreementLink } from '../aiAgreementLink';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);
const TOAST_DURATION = 5;

type InitialSettingProps = {
  initialIsModelTrainingAllowed: boolean;
  services: AiService[];
};

export const InitialSetting: FC<InitialSettingProps> = ({
  initialIsModelTrainingAllowed,
  services,
}) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const { send } = useErrorTrackingSystem();
  const { isPending: isAgreementPending, mutateAsync: agreeAiService } =
    useAgreeAiService();
  const [isModelTrainingAllowed, setIsModelTrainingAllowed] = useState(
    initialIsModelTrainingAllowed
  );

  const handleAgreement = useCallback(async () => {
    try {
      const result = await agreeAiService(isModelTrainingAllowed);

      if (result.isSuccessful) {
        notify.success({
          duration: TOAST_DURATION,
          title: t('ai_service_setting.agreement_success'),
        });
        return;
      }

      notify.error({ duration: TOAST_DURATION, title: result.message });
    } catch (error) {
      send(error instanceof Error ? error : String(error));
      notify.error({
        duration: TOAST_DURATION,
        title: t('ai_service_setting.agreement_error'),
      });
    }
  }, [agreeAiService, isModelTrainingAllowed, notify, send, t]);

  return (
    <div className={cx('container')} data-testid='initial-setting'>
      <Heading as='h2' weight='normal'>
        {t('ai_service_setting.initial_heading')}
      </Heading>
      <section className={cx('providedServices')}>
        <p>{t('ai_service_setting.provided_services_heading')}</p>
        <ul className={cx('providedServicesList')}>
          {services.map(({ description, name, alias_name, service_type }) => (
            <li key={service_type}>
              <p>
                {name}
                {alias_name && `(${alias_name})`}
              </p>
              <Caption icon={false} type='helper'>
                {description}
              </Caption>
            </li>
          ))}
        </ul>
      </section>
      <Divider style={{ margin: 0 }} />
      <div className={cx('agreementDescription')}>
        <p>{t('ai_service_setting.agreement_required')}</p>
        <AiAgreementLink />
      </div>
      <ModelTrainingSetting
        isAllowed={isModelTrainingAllowed}
        isInitial
        onChange={setIsModelTrainingAllowed}
      />
      <Caption icon={false} type='helper'>
        {t('ai_service_setting.operation_notice')}
      </Caption>
      <ButtonV2
        color='primary'
        isDisabled={isAgreementPending}
        onClick={handleAgreement}
        size='sm'
        type='button'
      >
        {t('ai_service_setting.agree_and_start_button')}
      </ButtonV2>
    </div>
  );
};
